import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, Check, Pencil, ShieldCheck, UserPlus } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import FormModal, { Field, inputClass } from '../../components/fieldWork/FormModal';
import InductionContent from '../../components/induction/InductionContent';
import InductionEditForm from '../../components/induction/InductionEditForm';
import SignOffDialog from '../../components/induction/SignOffDialog';
import { InductionAccessDenied, InductionSkeleton, StatusBadge } from '../../components/induction/InductionGuards';
import { useInductionSeats } from '../../components/induction/useInductionSeats';
import { BASIS_LABELS, personLabel } from '../../components/induction/inductionLabels';
import inductionService from '../../services/induction';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import type { EquipmentInductionDetail, InductionRecordRow } from '../../types/induction';

/** /inductions/equipment/:id — one induction: its content, machines and who is signed off. */
export default function InductionDetail() {
  const { id } = useParams<{ id: string }>();
  const tz = useOrgTimezone();
  const { loading: seatsLoading, hasAccess, canAssess } = useInductionSeats();
  const [ind, setInd] = useState<EquipmentInductionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [signing, setSigning] = useState(false);
  const [revoking, setRevoking] = useState<InductionRecordRow | null>(null);
  const [reason, setReason] = useState('');
  const [revokeSaving, setRevokeSaving] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setInd(await inductionService.get(parseInt(id, 10)));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the induction'));
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);

  const currentRefs = useMemo(
    () => new Set((ind?.records ?? []).filter(r => r.status === 'current' || r.status === 'expiring').map(r => r.person_ref)),
    [ind],
  );

  const revoke = async () => {
    if (!revoking) return;
    if (reason.trim().length < 3) { setRevokeError('Say why the sign-off is being revoked.'); return; }
    setRevokeSaving(true);
    setRevokeError(null);
    try {
      await inductionService.revoke(revoking.id, reason.trim());
      setRevoking(null);
      setNotice(`Revoked ${personLabel(revoking.person_name)}'s sign-off.`);
      load();
    } catch (e) {
      setRevokeError(errorMessage(e, 'Could not revoke the sign-off'));
    } finally {
      setRevokeSaving(false);
    }
  };

  if (seatsLoading) return <InductionSkeleton />;
  if (!hasAccess) return <InductionAccessDenied />;

  const records = ind?.records ?? [];

  return (
    <>
      <PageHeader
        title={ind?.title ?? 'Induction'}
        subtitle={ind ? `Content version ${ind.content_version}${ind.equipment_name ? ` · ${ind.equipment_name} only` : ''}` : undefined}
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
        actionButtons={canAssess && ind ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => setEditing(true)}><Pencil className="h-4 w-4" /> Edit</Button>
            <Button size="sm" onClick={() => setSigning(true)}><UserPlus className="h-4 w-4" /> Sign people off</Button>
          </div>
        ) : undefined}
      />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {loading && !ind && <p className="text-sm text-neutral-500">Loading…</p>}
        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 break-words">{error}</div>}
        {notice && <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-800 break-words">{notice}</div>}

        {ind && (
          <>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className={`rounded-full px-2 py-0.5 font-medium ${ind.is_required ? 'bg-primary-50 text-primary-700' : 'bg-neutral-100 text-neutral-600'}`}>
                {ind.is_required ? 'Required' : 'Optional'}
              </span>
              {!ind.active && <span className="rounded-full bg-neutral-200 px-2 py-0.5 font-medium text-neutral-700">Inactive</span>}
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-neutral-600">
                {ind.refresher_months ? `Refresher every ${ind.refresher_months} months` : 'No refresher'}
              </span>
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-neutral-600">
                Updated {formatDateInTz(ind.content_updated_at, tz)}
              </span>
              {ind.certificates_block && ind.required_certifications.length > 0 && (
                <span className="rounded-full bg-warning-50 px-2 py-0.5 font-medium text-warning-800">Certifications enforced</span>
              )}
            </div>

            <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
              <InductionContent induction={ind} requiredCertifications={ind.required_certifications} />
            </section>

            <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
              <h2 className="font-semibold text-neutral-900 mb-2">Machines covered ({ind.machines.length})</h2>
              {ind.machines.length === 0 ? (
                <p className="text-sm text-neutral-500">No machines of this type on the equipment register.</p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {ind.machines.map(m => (
                    <li key={m.id} className="max-w-full">
                      <Link to={`/equipment/${m.id}`} className="block max-w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm text-neutral-800 hover:bg-neutral-50 break-words">
                        {m.name}{m.status && m.status !== 'active' && <span className="text-neutral-400"> · {m.status}</span>}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
              <h2 className="font-semibold text-neutral-900 mb-3">People signed off ({records.length})</h2>
              {records.length === 0 ? (
                <p className="text-sm text-neutral-500">Nobody yet.</p>
              ) : (
                <ul className="divide-y divide-neutral-100">
                  {records.map(r => (
                    <li key={r.id} className="py-3 text-sm">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 flex-1 basis-48">
                          <div className="flex flex-wrap items-center gap-2">
                            <Link to={`/inductions/people/${r.person_ref}`} className="min-w-0 font-medium text-primary-700 hover:underline break-words">
                              {personLabel(r.person_name)}
                            </Link>
                            <StatusBadge status={r.status} />
                            {!r.worker_active && <span className="text-xs text-neutral-400">inactive</span>}
                          </div>
                          <div className="mt-1 text-neutral-600 break-words">
                            {BASIS_LABELS[r.basis]} · {[r.assessed_by_name, formatDateInTz(r.assessed_at, tz)].filter(Boolean).join(', ')}
                            {r.expires_on && ` · expires ${formatLocalDateInTz(r.expires_on, tz)}`}
                          </div>
                          <div className="mt-0.5 text-xs">
                            {r.acknowledged
                              ? <span className="inline-flex items-center gap-1 text-success-700"><Check className="h-3.5 w-3.5" />Acknowledged this version</span>
                              : <span className="text-warning-800">Not yet acknowledged this version</span>}
                          </div>
                          {r.notes && <p className="mt-1 text-xs text-neutral-500 break-words">{r.notes}</p>}
                          {r.missing_certificates.length > 0 && (
                            <p className="mt-1 flex items-start gap-1 text-xs text-error-700 break-words">
                              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
                              <span className="min-w-0">Missing: {r.missing_certificates.join(', ')}</span>
                            </p>
                          )}
                        </div>
                        {canAssess && (
                          <Button size="sm" variant="ghost" onClick={() => { setRevoking(r); setReason(''); setRevokeError(null); }}>
                            Revoke
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>

      {editing && ind && (
        <InductionEditForm
          induction={ind}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); setNotice('Saved.'); load(); }}
        />
      )}
      {signing && ind && (
        <SignOffDialog
          induction={ind}
          exclude={currentRefs}
          onClose={() => setSigning(false)}
          onDone={n => { setSigning(false); setNotice(`Signed off ${n} ${n === 1 ? 'person' : 'people'}.`); load(); }}
        />
      )}
      {revoking && (
        <FormModal
          title="Revoke sign-off"
          onClose={() => setRevoking(null)}
          onSubmit={revoke}
          submitLabel="Revoke"
          submitting={revokeSaving}
          error={revokeError}
        >
          <p className="text-sm text-neutral-700 break-words">
            <strong>{personLabel(revoking.person_name)}</strong> will no longer be able to operate these machines until
            they are signed off again.
          </p>
          <Field label="Reason" htmlFor="revoke-reason">
            <textarea id="revoke-reason" className={inputClass} rows={3} value={reason} onChange={e => setReason(e.target.value)} />
          </Field>
        </FormModal>
      )}
    </>
  );
}
